import { AxiosError } from 'axios';
import { logger } from '@/logging';

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  public readonly status?: number;
  public readonly detail?: string;
  public readonly code?: string;
  public readonly cause?: unknown;

  constructor(
    message: string,
    status?: number,
    detail?: string,
    code?: string,
    cause?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.code = code;
    this.cause = cause;

    // Maintains proper stack trace for where our error was thrown
    if (
      'captureStackTrace' in Error &&
      typeof Error.captureStackTrace === 'function'
    ) {
      Error.captureStackTrace(this, ApiError);
    }

    // Defer logging to avoid circular dependencies during initialization
    setTimeout(() => {
      logger.error('API Error', {
        message: this.message,
        status: this.status,
        detail: this.detail,
        code: this.code,
        userMessage: this.getUserMessage(),
        retryable: this.isRetryable(),
        stack: this.stack,
      });
    }, 0);
  }

  /**
   * Create ApiError from AxiosError
   */
  static fromAxiosError(error: AxiosError<unknown>): ApiError {
    const response = error.response;
    const responseData = response?.data as
      | { detail?: string | Array<{ loc: string[]; msg: string; type: string }>; message?: string; code?: string }
      | undefined;

    // Handle FastAPI validation errors (detail is an array)
    let message: string;
    let detail: string | undefined;

    if (responseData?.detail && Array.isArray(responseData.detail)) {
      // Format validation errors in a user-friendly way
      const validationErrors = responseData.detail.map((err: { loc: string[]; msg: string; type: string }) => {
        const fieldPath = err.loc.slice(1).join('.'); // Skip first element (usually 'body')
        return `${fieldPath}: ${err.msg}`;
      });
      message = 'Validation failed';
      detail = validationErrors.join('\n');
    } else {
      message = (typeof responseData?.detail === 'string' ? responseData.detail : responseData?.message) || error.message;
      detail = typeof responseData?.detail === 'string' ? responseData.detail : undefined;
    }

    const status = response?.status;
    const code = responseData?.code || error.code;

    return new ApiError(message, status, detail, code, error);
  }

  /**
   * Get user-friendly error message based on status code
   */
  getUserMessage(): string {
    if (this.detail) {
      return this.detail;
    }

    switch (this.status) {
      case 400:
        return 'Invalid request. Please check your input.';
      case 401:
        return 'Authentication required. Please sign in.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 422:
        return 'The provided data is invalid.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'An unexpected error occurred. Please try again.';
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return this.message || 'An error occurred. Please try again.';
    }
  }

  /**
   * Check if the error is retryable
   */
  isRetryable(): boolean {
    if (!this.status) {
      // Network errors are retryable
      return true;
    }

    // Retryable HTTP status codes
    const retryableStatuses = [408, 429, 500, 502, 503, 504];
    return retryableStatuses.includes(this.status);
  }

  /**
   * Convert to JSON for logging
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      status: this.status,
      detail: this.detail,
      code: this.code,
      stack: this.stack,
    };
  }
}

/**
 * Type guard to check if an error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Extract error message from unknown error
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.getUserMessage();
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred';
}
